import { useCallback, useEffect, useRef, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getUserActivity } from "../api/dashboard.api";
import type { UserActivityDataResponse } from "../types/dashboard-data.types";
import {
  DASHBOARD_USER_ACTIVITY_CACHE_KEY,
  readDashboardCache,
  writeDashboardCache,
} from "../utils/dashboardCache";
import type { DashboardQueryState } from "./dashboard-query.types";

export const useUserActivityData = (): DashboardQueryState<UserActivityDataResponse> => {
  const [data, setData] = useState<UserActivityDataResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { run } = useLatestRequest();
  const dataRef = useRef<UserActivityDataResponse | null>(null);
  const sessionRef = useRef(0);

  dataRef.current = data;

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      if (!forceRefresh) {
        const cached = readDashboardCache(DASHBOARD_USER_ACTIVITY_CACHE_KEY);
        if (cached) {
          setData(cached);
          setError(null);
          setIsLoading(false);
          setIsRefreshing(false);
          return;
        }
      }

      sessionRef.current += 1;
      const session = sessionRef.current;

      if (dataRef.current !== null) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        const result = await run((signal) => getUserActivity({ signal }));
        if (session !== sessionRef.current) return;
        if (result === undefined) return;
        writeDashboardCache(DASHBOARD_USER_ACTIVITY_CACHE_KEY, result);
        setData(result);
        setError(null);
      } catch (requestError) {
        if (session !== sessionRef.current) return;
        setError(getApiErrorMessage(requestError, "Failed to fetch user activity"));
      } finally {
        if (session === sessionRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [run],
  );

  const refetch = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    void fetchData();
    return () => {
      sessionRef.current += 1;
    };
  }, [fetchData]);

  return { data, isLoading, isRefreshing, error, refetch };
};
